/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Breakfast events (DynamoDB) — list, CRUD, and presigned banner upload.
 * @module logic/breakfastEvents
 */

import {
  assertAuthorizedApiResponse,
  fetchUserRoleData,
  getAuthJsonHeaders,
  normalizeAudienciaFromApi,
  parseExportDownloadUrl,
  resolveExpiredToken,
  showEventCreatedSuccess,
  uploadEventBannerFromDataUrl,
} from "../../utils/eventApi.js";
import {
  TIME_COLON_INDEX,
  TIME_INPUT_HH_MM_LENGTH,
  filterCalendarDayEventsForEventsList,
} from "../../utils/eventDateFilters.js";
import {
  filterNonCancelledEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import { getUser } from "../user/index.js";

const BREAKFAST_EVENT_NOT_FOUND = "Breakfast event not found";
const DATE_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";

export { showEventCreatedSuccess };

/**
 * Normalizes a time string for `input type="time"` (HH:mm). The API may return HH:mm:ss.
 * @param {string|null|undefined} value - Raw time from the API or form.
 * @returns {string|null} First five characters when parseable as HH:mm, otherwise trimmed string or null.
 */
const normalizeTimeForInput = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const s = String(value).trim();
  if (s.length >= TIME_INPUT_HH_MM_LENGTH && s[TIME_COLON_INDEX] === ":") {
    return s.slice(0, TIME_INPUT_HH_MM_LENGTH);
  }
  return s || null;
};

export const getUserRoleData = fetchUserRoleData;

/**
 * Fetches active breakfast events for the public Events page (`GET /breakfastEvent/active`).
 * Applies the same filters as registration events: `activo`, future `fechaEvento`, and
 * `fechaDisponibleSocios` / `fechaDisponibleNoSocios` based on `user.tipoDeUsuario`.
 * @param {string} userId - Reserved for parity with other event list helpers (unused; token identifies the caller).
 * @param {object} user - User record; `tipoDeUsuario === "4"` selects non-member availability rules.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @returns {Promise<Array<Record<string, unknown>>>} Filtered breakfast event objects, or `[]` on error.
 */
export const getBreakfastEventsForEventsList = async (
  userId,
  user,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/active/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getBreakfastEventsForEventsList(userId, user, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (!Array.isArray(data)) {
      return [];
    }
    return filterCalendarDayEventsForEventsList(data, user);
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Lists every breakfast event in DynamoDB (`GET /breakfastEvent/all`) for admin / collaborator screens.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @returns {Promise<Array<Record<string, unknown>>>} Raw array from the API, or `[]` if not an array or on error.
 */
export const getAllBreakfastEvents = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/all`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getAllBreakfastEvents(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    const list = Array.isArray(data) ? data : [];
    return sortEventsFuturesFirst(filterNonCancelledEvents(list));
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Loads permissions, current user, and the full breakfast-events list for the management list page
 * (breakfast events management list page).
 * @param {string} userId - Authenticated user id.
 * @param {Function} changePermissions - Passed to {@link getUserRoleData}.
 * @param {import("react-router").NavigateFunction} navigate - Passed to user and breakfast-event fetches.
 * @param {Function} setUser - React state setter for the loaded user object.
 * @returns {Promise<Array<Record<string, unknown>>>} Result of {@link getAllBreakfastEvents}, or `[]` on failure.
 */
export const getBreakfastEventsPageData = async (
  userId,
  changePermissions,
  navigate,
  setUser,
) => {
  try {
    await getUserRoleData(userId, changePermissions, navigate);
    const userData = await getUser(userId, navigate);
    setUser(userData);
    return await getAllBreakfastEvents(navigate);
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Formats a date-like value to `YYYY-MM-DD` in UTC for HTML date inputs.
 * @param {import("dayjs").Dayjs} dayjs - dayjs instance (injected to avoid circular imports).
 * @param {string|Date|null|undefined} value - API date string or similar.
 * @returns {string|null}
 */
const formatDateField = (dayjs, value) =>
  value ? dayjs(value).utc().format(DATE_FORMAT_YYYY_MM_DD) : null;

export { normalizeAudienciaFromApi };

/**
 * Loads one breakfast event and maps API fields into form-friendly shapes (UTC dates, normalized `hora`).
 * Invokes `setFormData` with the merged object; no-ops on 404 or non-OK responses.
 * @param {string} eventId - Breakfast event `_id`.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @param {import("dayjs").Dayjs} dayjs - dayjs instance for date formatting.
 * @param {Function} setFormData - React state setter receiving the mapped event object.
 * @returns {Promise<void>}
 */
export const getBreakfastEventDetails = async (
  eventId,
  navigate,
  dayjs,
  setFormData,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getBreakfastEventDetails(eventId, navigate, dayjs, setFormData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (data?.message === BREAKFAST_EVENT_NOT_FOUND) {
      return;
    }
    if (!response.ok) {
      return;
    }
    setFormData({
      ...data,
      fechaEvento: formatDateField(dayjs, data.fechaEvento),
      fechaDisponibleSocios: formatDateField(dayjs, data.fechaDisponibleSocios),
      fechaDisponibleNoSocios: formatDateField(
        dayjs,
        data.fechaDisponibleNoSocios,
      ),
      fechaLimiteRegistro: formatDateField(dayjs, data.fechaLimiteRegistro),
      hora: normalizeTimeForInput(data.hora),
      audiencia: normalizeAudienciaFromApi(data.audiencia),
    });
  } catch (error) {
    console.error(error);
  }
};

/**
 * Creates or updates a breakfast event (`POST /breakfastEvent` or `PUT /breakfastEvent/{eventId}`).
 * When `eventData.imagenBanner` is a new base64 data URL and the API returns `presignedUrl`, uploads the file to S3.
 * @param {Record<string, unknown>} eventData - Payload including `userId`, event fields, optional `nombreImagen` / `formatoImagen`, and banner data URL when changed.
 * @param {string|undefined|null} eventId - Existing event `_id` for update; omit or falsy for create.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @param {Function} setSaving - React setter for in-flight save state (always cleared in `finally`).
 * @param {Function} setEventData - React setter called with `data.event` on success.
 * @param {Function} setHasChanges - React setter; set to `false` after a successful save.
 * @returns {Promise<{ ok: boolean, wasCreate?: boolean, event?: Record<string, unknown> }|undefined>}
 *   `{ ok: true, wasCreate, event }` on success, `{ ok: false }` on failure, or `undefined` if redirected after token expiry.
 */
export const submitBreakfastEvent = async (
  eventData,
  eventId,
  navigate,
  setSaving,
  setEventData,
  setHasChanges,
) => {
  setSaving(true);
  const wasCreate = !eventId;
  const { Buffer } = await import("buffer");
  try {
    const body = {
      ...eventData,
      audiencia: normalizeAudienciaFromApi(eventData.audiencia),
      nombreImagen: eventData.nombreImagen || undefined,
    };

    const url = eventId
      ? `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}`
      : `${process.env.REACT_APP_ROOT_API}breakfastEvent`;
    const method = eventId ? "PUT" : "POST";
    const response = await fetch(url, {
      method,
      headers: getAuthJsonHeaders(),
      body: JSON.stringify(body),
    });

    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      submitBreakfastEvent(
        eventData,
        eventId,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { ok: false };
      }
      return tokenResult.result;
    }

    if (response.ok && data.event) {
      if (data.presignedUrl && eventData.imagenBanner) {
        await uploadEventBannerFromDataUrl(
          data.presignedUrl,
          eventData.imagenBanner,
          eventData.formatoImagen,
          Buffer,
        );
      }
      setEventData(data.event);
      setHasChanges(false);
      return { ok: true, wasCreate, event: data.event };
    }
    return { ok: false };
  } catch (error) {
    console.error("Error submitting breakfast event:", error);
    return { ok: false };
  } finally {
    setSaving(false);
  }
};

/**
 * Prepares the breakfast-event form: refreshes permissions, then loads the event when `eventId` is provided.
 * Always toggles `setIsLoading` (true at start, false in `finally`).
 * @param {string|undefined|null} eventId - Breakfast event `_id` for edit mode; omit for create.
 * @param {string} userId - Authenticated user id for {@link getUserRoleData}.
 * @param {Function} changePermissions - Passed to {@link getUserRoleData}.
 * @param {import("react-router").NavigateFunction} navigate - Passed to permission and detail loaders.
 * @param {import("dayjs").Dayjs} dayjs - Passed to {@link getBreakfastEventDetails}.
 * @param {Function} setFormData - Passed to {@link getBreakfastEventDetails} when editing.
 * @param {Function} setIsLoading - React setter for full-page or backdrop loading state.
 * @returns {Promise<void>}
 */
export const initializeBreakfastEventForm = async (
  eventId,
  userId,
  changePermissions,
  navigate,
  dayjs,
  setFormData,
  setIsLoading,
) => {
  try {
    setIsLoading(true);
    await getUserRoleData(userId, changePermissions, navigate);
    if (eventId) {
      await getBreakfastEventDetails(eventId, navigate, dayjs, setFormData);
    }
  } catch (error) {
    console.error("Error initializing breakfast event form:", error);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Downloads the invitees list file for a breakfast event.
 * @param {string} eventId - Breakfast event `_id`.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @returns {Promise<string|null>} The S3 URL of the invitees list file, or `null` on error.
 */
export const downloadInviteesListFile = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/invitations-file-url`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      downloadInviteesListFile(eventId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }

    await assertAuthorizedApiResponse(response, data, navigate);
    if (!response.ok) {
      return null;
    }
    return parseExportDownloadUrl(data);
  } catch (error) {
    console.error(error);
    return null;
  }
};

/** Builds breakfast event participating companies by status URL with optional lastKey for pagination. */
const buildPageDataUrl = (eventId, status, lastKey) => {
  const base = `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/participatingCompanies/${status}`;
  if (!lastKey) {
    return base;
  }
  // Backend returns lastEvaluatedKey already URL-encoded; send as-is to avoid double-encoding.
  const keyParam =
    typeof lastKey === "string"
      ? lastKey
      : encodeURIComponent(JSON.stringify(lastKey));
  return `${base}?lastKey=${keyParam}`;
};

/**
 * Lists participating companies for a breakfast event by status.
 * @param {string} eventId - Breakfast event `_id`.
 * @param {string} status - Status of the participating companies.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @returns {Promise<{ companies: Array, lastEvaluatedKey: string|null }>} Object containing the participating companies and the last evaluated key.
 */
export const getBreakfastEventParticipatingCompaniesByStatus = async (
  eventId,
  status,
  navigate,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      buildPageDataUrl(eventId, status, null),
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getBreakfastEventParticipatingCompaniesByStatus(
        eventId,
        status,
        navigate,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return {
          companies: [],
          lastEvaluatedKey: null,
        };
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      throw new Error(data?.message);
    }

    return {
      companies: Array.isArray(data.items) ? data.items : [],
      lastEvaluatedKey: data.lastEvaluatedKey ?? null,
    };
  } catch (error) {
    console.error(error);
    return {
      companies: [],
      lastEvaluatedKey: null,
    };
  }
};

/**
 * Fetches more participating companies for a breakfast event by status.
 * @param {string} eventId - Breakfast event `_id`.
 * @param {string} status - Status of the participating companies.
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @param {string|Object} lastKey - Last evaluated key from the previous page response
 * @returns {Promise<{ companies: Array, lastEvaluatedKey: string|null }>} Object containing the participating companies and the last evaluated key.
 */
export const fetchMoreParticipatingCompaniesByStatus = async (
  eventId,
  status,
  navigate,
  lastKey,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      buildPageDataUrl(eventId, status, lastKey),
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      fetchMoreParticipatingCompaniesByStatus(
        eventId,
        status,
        navigate,
        lastKey,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { companies: [], lastEvaluatedKey: null };
      }
      return tokenResult.result;
    }

    return {
      companies: Array.isArray(data.items) ? data.items : [],
      lastEvaluatedKey: data.lastEvaluatedKey ?? null,
    };
  } catch (error) {
    console.error("Error fetching more participating companies:", error);
    return { companies: [], lastEvaluatedKey: null };
  }
};

export const markCompanyAsInterested = async (eventId, navigate) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/interest`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      markCompanyAsInterested(eventId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      throw new Error(data?.message);
    }
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const countBreakfastEventCompanies = async (filters, navigate) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(filters),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/audience-count`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      countBreakfastEventCompanies(filters, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return 0;
      }
      return tokenResult.result;
    }

    if (!response.ok) {
      throw new Error(data?.message);
    }
    return data?.count ?? 0;
  } catch (error) {
    console.error(error);
    return 0;
  }
};

export const confirmBreakfastEventRegistrations = async (
  eventId,
  selectedIds,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      selectedCompanyIds: selectedIds,
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/confirm-registration`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      confirmBreakfastEventRegistrations(eventId, selectedIds, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      throw new Error(data?.message);
    }
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const rejectBreakfastEventParticipatingCompany = async (
  eventId,
  companyId,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/participatingCompany/${companyId}/reject`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      rejectBreakfastEventParticipatingCompany(eventId, companyId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      throw new Error(data?.message);
    }
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetches active breakfast events for the public Events page (`GET /breakfastEvent/active`).
 * @param {import("react-router").NavigateFunction} navigate - Used for refresh/sign-out on expired token.
 * @returns {Promise<Array<Record<string, unknown>>>} Filtered breakfast event objects, or `[]` on error.
 */
export const getActiveBreakfastEvents = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/active`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getActiveBreakfastEvents(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }

    return filterNonCancelledEvents(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Fetches attendees for a Breakfast event.
 * @async
 * @param {Object} event - Breakfast event object with _id
 * @param {Function} navigate - React Router navigation function
 * @param {Function} setAttendanceData - State setter for attendance data
 * @returns {Promise<void>}
 */
export const getBreakfastEventAttendees = async (
  event,
  navigate,
  setAttendanceData,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${event._id}/attendees`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getBreakfastEventAttendees(event, navigate, setAttendanceData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    setAttendanceData(response.ok && Array.isArray(data) ? data : []);
  } catch (error) {
    console.error(error);
    setAttendanceData([]);
  }
};

/**
 * Updates the attendance status for a breakfast event.
 * @param {string} userId - User identifier
 * @param {string} eventId - Breakfast event identifier
 * @param {boolean} newStatus - New attendance status
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Object>} Updated attendance data
 * @throws {Error} If update fails
 */
export const updateBreakfastEventAttendance = async (
  userId,
  eventId,
  newStatus,
  navigate,
) => {
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idUsuario: userId,
      idEventoDesayuno: eventId,
      estatus: newStatus,
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}breakfastEvent/${eventId}/attendee/status`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      updateBreakfastEventAttendance(userId, eventId, newStatus, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      throw new Error(data?.message);
    }
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
